import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import * as mui from '@mui/material';
import { SideBar } from '../../layout';
import SearchAppBar from '../../common/Appbar';
import Separator from '../../layout/Separator';
import ResponsiveContainer from '../../layout/Container';
import CloudUploadIcon from '@mui/icons-material/CloudUpload';
import StorageIcon from '@mui/icons-material/Storage';
import ScheduleIcon from '@mui/icons-material/Schedule';
import ContactMailIcon from '@mui/icons-material/ContactMail';
import ArticleIcon from '@mui/icons-material/Article';
import AccessTimeIcon from '@mui/icons-material/AccessTime';
import ArrowForwardIcon from '@mui/icons-material/ArrowForward';
import HistoryIcon from '@mui/icons-material/History';
import HomeIcon from '@mui/icons-material/Home';
import HRService from '../../../services/HrService';
import SFTPService from '../../../services/SFTPService';
import CompanyService from '../../../services/CompanyService';
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';
import ErrorOutlineIcon from '@mui/icons-material/ErrorOutline';
import VerifiedUserIcon from '@mui/icons-material/VerifiedUser';

const HRDataManagement = () => {
  const navigate = useNavigate();
  const [importLogs, setImportLogs] = useState([]);
  const [sftpConfigs, setSftpConfigs] = useState([]);
  const [jobSchedules, setJobSchedules] = useState([]);
  const [stats, setStats] = useState({
    totalImports: 0,
    successfulImports: 0,
    totalRecords: 0,
    lastImportDate: null
  });
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchData = async () => {
      try {
        // Fetch import logs
        const logsData = await HRService.fetchHRImportLogs();
        setImportLogs(logsData || []);
        
        // Calculate stats
        if (logsData && logsData.length > 0) {
          const successful = logsData.filter(log => log.JOB_COMPLETE).length;
          
          // Calculate total records, ensuring all values are numbers
          const totalRecords = logsData.reduce((sum, log) => {
            const count = log.SOURCE_LINE_COUNT;
            // Convert to number if it's a string, use 0 if null/undefined/"NULL"/etc
            const recordCount = typeof count === 'number' ? count : 
                                (count && !isNaN(parseInt(count)) ? parseInt(count) : 0);
            return sum + recordCount;
          }, 0);
          
          // Sort logs by date - handle both field names
          const sortedLogs = logsData.sort((a, b) => {
            const dateA = new Date(a.JOB_DATE || a.IMPORT_DATE);
            const dateB = new Date(b.JOB_DATE || b.IMPORT_DATE);
            return dateB - dateA;
          });
          
          const lastImport = sortedLogs[0];
          
          setStats({
            totalImports: logsData.length,
            successfulImports: successful,
            totalRecords: totalRecords,
            lastImportDate: lastImport ? (lastImport.JOB_DATE || lastImport.IMPORT_DATE) : null
          });
        }
        
        // Fetch SFTP configs
        const configData = await SFTPService.fetchSFTP();
        setSftpConfigs(configData || []);
        
        // Fetch job schedules
        const jobsData = await SFTPService.fetchHRJobs();
        setJobSchedules(jobsData || []);
      } catch (error) {
        console.error('Error fetching HR data:', error);
      } finally {
        setLoading(false);
      }
    };
    
    fetchData();
  }, []);
  
  // Format numbers for display
  const formatNumber = (num) => {
    if (num === undefined || num === null) return '0';
    
    // For large numbers, use compact notation
    if (num >= 1000) {
      return new Intl.NumberFormat('en-US', { 
        notation: 'compact',
        maximumFractionDigits: 1 
      }).format(num);
    }
    
    // For smaller numbers, use regular formatting
    return new Intl.NumberFormat('en-US').format(num);
  };

  // Format date for display
  const formatDate = (dateString) => {
    if (!dateString) return 'Never';
    
    try {
      // Try parsing the date normally first
      const date = new Date(dateString);
      
      // Check if the date is valid
      if (isNaN(date.getTime())) {
        // If date is invalid, try parsing it manually if it's in format 1/1/20
        if (typeof dateString === 'string') {
          const parts = dateString.split('/');
          if (parts.length === 3) {
            // Handle 2-digit years (assuming 20xx for brevity)
            let year = parseInt(parts[2]);
            if (year < 100) {
              year += year < 50 ? 2000 : 1900;
            }
            
            const month = parseInt(parts[0]) - 1; // JS months are 0-indexed
            const day = parseInt(parts[1]);
            
            const manualDate = new Date(year, month, day);
            if (!isNaN(manualDate.getTime())) {
              return manualDate.toLocaleString();
            }
          }
        }
        
        // If we couldn't parse it, just return the original string
        return dateString;
      }
      
      return date.toLocaleString();
    } catch (error) {
      console.error('Error formatting date:', error);
      return dateString || 'Unknown';
    }
  };
  
  // Define action cards
  const actionCards = [
    {
      title: 'Manual Upload',
      description: 'Upload HR records directly from CSV files',
      icon: <CloudUploadIcon sx={{ fontSize: 40 }} />,
      path: '/interfaces/hr/upload',
      primary: true,
      color: 'primary.main',
      actions: [
        { label: 'Upload Records', onClick: () => navigate('/interfaces/hr/upload') },
        { label: 'Download Template', onClick: () => HRService.downloadHRTemplate() }
      ]
    },
    {
      title: 'SFTP Configuration',
      description: 'Configure connection to HR data on SFTP servers',
      icon: <StorageIcon sx={{ fontSize: 40 }} />,
      path: '/interfaces/hr/sftp',
      status: sftpConfigs.length > 0 ? (
        sftpConfigs.some(config => config.SETUP_COMPLETE) ? 'Configured' : 'Incomplete'
      ) : 'Not Configured',
      statusColor: sftpConfigs.length > 0 ? (
        sftpConfigs.some(config => config.SETUP_COMPLETE) ? 'success.main' : 'warning.main'
      ) : 'error.main',
      color: 'secondary.main'
    },
    {
      title: 'Job Scheduling',
      description: 'Schedule automated HR data import jobs',
      icon: <ScheduleIcon sx={{ fontSize: 40 }} />,
      path: '/interfaces/hr/jobs',
      status: jobSchedules.length > 0 ? `${jobSchedules.length} Job(s)` : 'No Jobs',
      statusColor: jobSchedules.length > 0 ? 'success.main' : 'warning.main',
      color: 'info.main'
    },
    {
      title: 'Job Status & Control',
      description: 'Monitor and control running HR data import jobs',
      icon: <AccessTimeIcon sx={{ fontSize: 40 }} />,
      path: '/interfaces/hr/job-status',
      status: jobSchedules.length > 0 ? 'Available' : 'No Jobs',
      statusColor: jobSchedules.length > 0 ? 'success.main' : 'warning.main',
      color: 'warning.main'
    },
    {
      title: 'Change History',
      description: 'Track department and job title changes over time',
      icon: <HistoryIcon sx={{ fontSize: 40 }} />,
      path: '/interfaces/hr/changes',
      status: null,
      color: 'success.main'
    },
    {
      title: 'Audit HR Records',
      description: 'Perform completeness and accuracy checks on HR records',
      icon: <VerifiedUserIcon sx={{ fontSize: 40 }} />,
      path: '/interfaces/hr/audit',
      primary: false,
      color: 'info.main',
      status: 'Audit Ready',
      statusColor: 'info.main',
      actions: [
        { label: 'View Audit Records', onClick: () => navigate('/interfaces/hr/audit') },
        { label: 'Export Audit Report', onClick: () => navigate('/interfaces/hr/audit?export=true') }
      ]
    }
  ];

  const customMainContent = (
    <ResponsiveContainer>
      <mui.Breadcrumbs aria-label="breadcrumb">
        <mui.Link underline="hover" color="inherit" href="/Dashboard" sx={{ display: 'flex', alignItems: 'center' }}>
          <HomeIcon sx={{ mr: 0.5, fontSize: '1.1rem' }} />
          Home
        </mui.Link>
        <mui.Link underline="hover" color="inherit" href="/Dashboard">
          Dashboard
        </mui.Link>
        <mui.Typography color="text.primary">HR Data Management</mui.Typography>
      </mui.Breadcrumbs>

      <SearchAppBar title="HR Data Management" icon={<ContactMailIcon />} />
      <mui.Typography sx={{ marginTop: '20px' }} variant="subtitle2" gutterBottom>
        Import, configure, and manage HR data from various sources
      </mui.Typography>

      <Separator />

      {loading ? (
        <mui.Box sx={{ display: 'flex', justifyContent: 'center', my: 4 }}>
          <mui.CircularProgress />
        </mui.Box>
      ) : (
        <>
          {/* Stats Overview */}
          <mui.Grid container spacing={3} sx={{ mt: 1, mb: 4 }}>
            <mui.Grid item xs={12} sm={6} md={3}>
              <mui.Paper elevation={2} sx={{ p: 2, height: '100%', display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center' }}>
                <mui.Typography variant="h4" color="primary.main" sx={{ fontWeight: 'bold' }}>
                  {formatNumber(stats.totalImports)}
                </mui.Typography>
                <mui.Typography variant="body2" color="text.secondary">
                  Total Imports
                </mui.Typography>
              </mui.Paper>
            </mui.Grid>
            
            <mui.Grid item xs={12} sm={6} md={3}>
              <mui.Paper elevation={2} sx={{ p: 2, height: '100%', display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center' }}>
                <mui.Typography variant="h4" color="success.main" sx={{ fontWeight: 'bold' }}>
                  {formatNumber(stats.successfulImports)}
                </mui.Typography>
                <mui.Typography variant="body2" color="text.secondary">
                  Successful Imports
                </mui.Typography>
              </mui.Paper>
            </mui.Grid>
            
            <mui.Grid item xs={12} sm={6} md={3}>
              <mui.Paper elevation={2} sx={{ p: 2, height: '100%', display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center' }}>
                <mui.Typography variant="h4" color="info.main" sx={{ fontWeight: 'bold' }}>
                  {formatNumber(stats.totalRecords)}
                </mui.Typography>
                <mui.Typography variant="body2" color="text.secondary">
                  Total Records
                </mui.Typography>
              </mui.Paper>
            </mui.Grid>
            
            <mui.Grid item xs={12} sm={6} md={3}>
              <mui.Paper elevation={2} sx={{ p: 2, height: '100%', display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center' }}>
                <mui.Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                  <AccessTimeIcon color="primary" sx={{ mr: 1 }} />
                  <mui.Typography variant="body1" fontWeight="medium">
                    {formatDate(stats.lastImportDate)}
                  </mui.Typography>
                </mui.Box>
                <mui.Typography variant="body2" color="text.secondary">
                  Last Import
                </mui.Typography>
              </mui.Paper>
            </mui.Grid>
          </mui.Grid>

          {/* Action Cards */}
          <mui.Grid container spacing={3} sx={{ mb: 4 }}>
            {actionCards.map((card, index) => (
              <mui.Grid item xs={12} md={4} key={index}>
                <mui.Paper
                  elevation={3}
                  sx={{
                    p: 3,
                    height: '100%',
                    display: 'flex',
                    flexDirection: 'column',
                    position: 'relative',
                    borderTop: card.primary ? `4px solid ${card.color}` : 'none',
                    '&:hover': {
                      boxShadow: 6,
                      transform: 'translateY(-4px)',
                      transition: 'transform 0.3s ease-in-out, box-shadow 0.3s ease-in-out'
                    },
                    transition: 'transform 0.3s ease-in-out, box-shadow 0.3s ease-in-out'
                  }}
                >
                  <mui.Box sx={{ 
                    display: 'flex', 
                    justifyContent: 'space-between', 
                    alignItems: 'flex-start',
                    mb: 2
                  }}>
                    <mui.Box sx={{ 
                      width: 60, 
                      height: 60, 
                      borderRadius: '50%',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      bgcolor: `${card.color}15`,
                      color: card.color
                    }}>
                      {card.icon}
                    </mui.Box>
                    
                    {card.status && (
                      <mui.Chip 
                        label={card.status} 
                        size="small"
                        sx={{ 
                          color: card.statusColor,
                          bgcolor: `${card.statusColor}15`,
                          fontWeight: 'medium'
                        }}
                      />
                    )}
                  </mui.Box>
                  
                  <mui.Typography variant="h6" gutterBottom>
                    {card.title}
                  </mui.Typography>
                  
                  <mui.Typography variant="body2" color="text.secondary" sx={{ mb: 2, flexGrow: 1 }}>
                    {card.description}
                  </mui.Typography>
                  
                  <mui.Box sx={{ mt: 'auto' }}>
                    {card.actions ? (
                      <mui.Stack direction="row" spacing={1}>
                        {card.actions.map((action, actionIndex) => (
                          <mui.Button
                            key={actionIndex}
                            variant={actionIndex === 0 ? "contained" : "outlined"}
                            color={actionIndex === 0 ? "primary" : "secondary"}
                            onClick={action.onClick}
                            size="small"
                          >
                            {action.label}
                          </mui.Button>
                        ))}
                      </mui.Stack>
                    ) : (
                      <mui.Button
                        variant="contained"
                        color="primary"
                        onClick={() => navigate(card.path)}
                        fullWidth
                      >
                        Configure
                      </mui.Button>
                    )}
                  </mui.Box>
                </mui.Paper>
              </mui.Grid>
            ))}
          </mui.Grid>
          
          {/* Recent Imports */}
          <mui.Box sx={{ mb: 4 }}>
            <mui.Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
              <mui.Typography variant="h6" sx={{ display: 'flex', alignItems: 'center' }}>
                <ArticleIcon sx={{ mr: 1 }} />
                Recent Imports
              </mui.Typography>
              
              {importLogs.length > 5 && (
                <mui.Button 
                  size="small"
                  endIcon={<ArrowForwardIcon />}
                  onClick={() => navigate('/interfaces/hr/details')}
                >
                  View All
                </mui.Button>
              )}
            </mui.Box>
            
            {importLogs.length > 0 ? (
              <mui.Paper elevation={2} sx={{ borderRadius: 2, overflow: 'hidden' }}>
                <mui.List sx={{ p: 0 }}>
                  {importLogs.slice(0, 5).map((log) => (
                    <mui.ListItem 
                      key={log.id}
                      disableGutters
                      divider
                      sx={{ 
                        px: 2, 
                        py: 1.5,
                        '&:hover': { bgcolor: 'rgba(0, 0, 0, 0.03)' }
                      }}
                    >
                      <mui.ListItemIcon sx={{ minWidth: 40 }}>
                        {log.JOB_COMPLETE ? (
                          <mui.Avatar 
                            sx={{ 
                              width: 32, 
                              height: 32, 
                              bgcolor: 'success.light',
                              color: 'success.contrastText' 
                            }}
                          >
                            <CheckCircleOutlineIcon fontSize="small" />
                          </mui.Avatar>
                        ) : (
                          <mui.Avatar 
                            sx={{ 
                              width: 32, 
                              height: 32, 
                              bgcolor: 'error.light',
                              color: 'error.contrastText' 
                            }}
                          >
                            <ErrorOutlineIcon fontSize="small" />
                          </mui.Avatar>
                        )}
                      </mui.ListItemIcon>
                      
                      <mui.ListItemText
                        primary={
                          <mui.Box sx={{ display: 'flex', alignItems: 'center' }}>
                            <mui.Typography 
                              variant="body2" 
                              fontWeight="medium" 
                              sx={{ mr: 1 }}
                            >
                              {log.JOB_FILE_NAME || log.FILE_NAME || 'Unknown File'}
                            </mui.Typography>
                            <mui.Chip
                              label={log.JOB_NAME || 'Import'}
                              size="small"
                              variant="outlined"
                              sx={{ height: 20, fontSize: '0.7rem' }}
                            />
                          </mui.Box>
                        }
                        secondary={
                          <mui.Typography variant="caption" color="text.secondary">
                            {formatDate(log.JOB_DATE || log.IMPORT_DATE)}
                          </mui.Typography>
                        }
                      />
                      
                      <mui.Box sx={{ display: 'flex', alignItems: 'center' }}>
                        <mui.Typography variant="body2" sx={{ mr: 2, fontWeight: 'medium' }}>
                          {formatNumber(log.SOURCE_LINE_COUNT || 0)} rows | {formatNumber(log.DESTI_LINE_COUNT || 0)} records
                        </mui.Typography>
                        
                        <mui.Chip 
                          label={log.JOB_COMPLETE ? "Complete" : "Failed"}
                          color={log.JOB_COMPLETE ? "success" : "error"}
                          size="small"
                          variant="outlined"
                          sx={{ minWidth: 75, fontWeight: 500 }}
                        />
                      </mui.Box>
                    </mui.ListItem>
                  ))}
                </mui.List>
              </mui.Paper>
            ) : (
              <mui.Paper elevation={2} sx={{ p: 4, textAlign: 'center', borderRadius: 2 }}>
                <mui.Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
                  <mui.Box sx={{ mb: 2, color: 'text.disabled' }}>
                    <ArticleIcon sx={{ fontSize: 48 }} />
                  </mui.Box>
                  <mui.Typography variant="body1" color="text.secondary" gutterBottom>
                    No import history available
                  </mui.Typography>
                  <mui.Typography variant="body2" color="text.secondary" sx={{ mb: 3, maxWidth: 400 }}>
                    Start by uploading HR records or configuring SFTP to automatically import data
                  </mui.Typography>
                  <mui.Button
                    variant="outlined"
                    color="primary"
                    onClick={() => navigate('/interfaces/hr/upload')}
                  >
                    Upload HR Records
                  </mui.Button>
                </mui.Box>
              </mui.Paper>
            )}
          </mui.Box>
        </>
      )}
    </ResponsiveContainer>
  );

  return (
    <div>
      <SideBar mainContent={customMainContent} />
    </div>
  );
};

export default HRDataManagement; 